/**
 * 部品依頼の通知先（分類グループごと） — 部署管理 page card.
 * Compact rows (name + summary) with a focused edit dialog: searchable
 * user picker + searchable category picker. Requests route by
 * category → group → users get email + chat DM and PIC inbox access.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRinging, PencilSimple, Plus, TrashSimple, Check, MagnifyingGlass, Tag, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartsRequestNotifyCard({ canManage }) {
  const [groups, setGroups] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, name, userIds, catIds}

  const load = async () => {
    try {
      const [gRes, cRes, catRes] = await Promise.all([
        axios.get(`${API}/shizai/pr-groups`),
        axios.get(`${API}/mfg/departments/notify-candidates`).catch(() => ({ data: [] })),
        axios.get(`${API}/shizai/order-categories`).catch(() => ({ data: [] })),
      ]);
      setGroups(gRes.data || []);
      setCandidates(cRes.data || []);
      setCategories((catRes.data || []).filter((c) => c.active !== false));
    } catch (_e) {
      toast.error('分類グループの読み込みに失敗しました');
    }
  };
  useEffect(() => { load(); }, []);

  const userName = (uid) => {
    const u = candidates.find((x) => x.id === uid);
    return u?.name || u?.email || '?';
  };
  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const removeGroup = async (g) => {
    if (!window.confirm(`グループ「${g.name}」を削除しますか？（依頼データは消えません）`)) return;
    try {
      await axios.delete(`${API}/shizai/pr-groups/${g.id}`);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  return (
    <div className="space-y-4" data-testid="parts-request-notify-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <BellRinging size={22} weight="duotone" className="text-orange-500" />
            部品依頼の通知先
          </h3>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            受注分類をグループにまとめ、グループごとに通知先を割り当てます。該当する依頼が届くと担当者へメール＋チャットDMで通知されます。
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setEditing({ name: '', userIds: [], catIds: [] })}
            className="bg-teal-700 hover:bg-teal-800" data-testid="prn-add-group">
            <Plus size={14} className="mr-1" /> グループを追加
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 divide-y divide-zinc-200">
        {groups.length === 0 && (
          <p className="px-4 py-8 text-sm text-zinc-400 text-center" data-testid="prn-empty">
            グループがまだありません
          </p>
        )}
        {groups.map((g) => (
          <div key={g.id} className="px-4 py-3 flex items-center gap-4" data-testid={`prn-group-${g.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-zinc-900">{g.name}</span>
                <span className="text-[11px] text-zinc-400">
                  {(g.category_ids || []).length}分類 · {(g.notify_user_ids || []).length}人
                </span>
              </div>
              <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px]">
                {(g.notify_user_ids || []).slice(0, 3).map((uid) => (
                  <span key={uid} className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-100">
                    {userName(uid)}
                  </span>
                ))}
                {(g.notify_user_ids || []).length > 3 && (
                  <span className="text-zinc-400">+{g.notify_user_ids.length - 3}</span>
                )}
                <span className="text-zinc-300">|</span>
                {(g.category_ids || []).slice(0, 4).map((cid) => (
                  <span key={cid} className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">
                    {catById[cid]?.symbol || catById[cid]?.name || '?'}
                  </span>
                ))}
                {(g.category_ids || []).length > 4 && (
                  <span className="text-zinc-400">+{g.category_ids.length - 4}</span>
                )}
                {(g.category_ids || []).length === 0 && <span className="text-zinc-300 italic">分類未設定</span>}
              </div>
            </div>
            {canManage && (
              <div className="shrink-0 flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-8 px-2 text-zinc-500 hover:text-zinc-800"
                  onClick={() => setEditing({ id: g.id, name: g.name, userIds: g.notify_user_ids || [], catIds: g.category_ids || [], sort_order: g.sort_order })}
                  data-testid={`prn-edit-${g.id}`}>
                  <PencilSimple size={15} />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-zinc-400 hover:text-rose-600"
                  onClick={() => removeGroup(g)} data-testid={`prn-delete-${g.id}`}>
                  <TrashSimple size={15} />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <GroupDialog
          editing={editing}
          candidates={candidates}
          categories={categories}
          groups={groups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function GroupDialog({ editing, candidates, categories, groups, onClose, onSaved }) {
  const [name, setName] = useState(editing.name);
  const [userIds, setUserIds] = useState(editing.userIds);
  const [catIds, setCatIds] = useState(editing.catIds);
  const [saving, setSaving] = useState(false);

  const otherGroupOf = (cid) => {
    const g = groups.find((x) => x.id !== editing.id && (x.category_ids || []).includes(cid));
    return g?.name || null;
  };

  const save = async () => {
    if (!name.trim()) { toast.error('グループ名を入力してください'); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), category_ids: catIds, notify_user_ids: userIds, sort_order: editing.sort_order ?? 100 };
      if (editing.id) await axios.put(`${API}/shizai/pr-groups/${editing.id}`, body);
      else await axios.post(`${API}/shizai/pr-groups`, body);
      toast.success(`「${name.trim()}」を保存しました`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{editing.id ? 'グループを編集' : 'グループを追加'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="グループ名（例: H課ヒーター）" autoFocus
            data-testid="prn-group-name-input" />
          <ChipPicker
            icon={<Users size={13} weight="bold" />}
            label="通知先ユーザー"
            items={candidates.map((u) => ({ id: u.id, label: u.name || u.email, sub: u.email }))}
            selected={userIds}
            onToggle={(id) => setUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
            activeClass="bg-orange-500 text-white border-orange-500"
            testPrefix="prn-user"
          />
          <ChipPicker
            icon={<Tag size={13} weight="bold" />}
            label="受注分類"
            items={categories.map((c) => {
              const other = !catIds.includes(c.id) ? otherGroupOf(c.id) : null;
              return {
                id: c.id,
                label: `${c.name}${c.symbol ? ` (${c.symbol})` : ''}`,
                sub: other ? `現在「${other}」に所属 — 選択で移動` : c.code,
                muted: !!other,
              };
            })}
            selected={catIds}
            onToggle={(id) => setCatIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
            activeClass="bg-sky-600 text-white border-sky-600"
            testPrefix="prn-cat"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>キャンセル</Button>
          <Button size="sm" onClick={save} disabled={saving}
            className="bg-teal-700 hover:bg-teal-800" data-testid="prn-save">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Searchable chip list — selected items pinned first, rest filtered by query.
function ChipPicker({ icon, label, items, selected, onToggle, activeClass, testPrefix }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const match = (it) => !s || `${it.label} ${it.sub || ''}`.toLowerCase().includes(s);
    const sel = items.filter((it) => selected.includes(it.id));
    const rest = items.filter((it) => !selected.includes(it.id) && match(it));
    return [...sel, ...rest];
  }, [items, selected, q]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-zinc-600 flex items-center gap-1">{icon} {label}
          <span className="text-zinc-400 font-normal">（{selected.length}）</span>
        </span>
      </div>
      <div className="relative mb-2">
        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="検索..."
          className="h-8 pl-8 text-xs" data-testid={`${testPrefix}-search`} />
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto rounded-md border border-zinc-100 bg-zinc-50/50 p-2">
        {filtered.length === 0 && <span className="text-xs text-zinc-400 italic px-1">該当なし</span>}
        {filtered.map((it) => {
          const sel = selected.includes(it.id);
          return (
            <button key={it.id} type="button" onClick={() => onToggle(it.id)}
              data-testid={`${testPrefix}-${it.id}`}
              title={it.sub || ''}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                sel ? activeClass
                    : it.muted ? 'bg-white text-zinc-400 border-zinc-200 hover:border-zinc-400'
                               : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500'
              }`}>
              {sel && <Check size={12} weight="bold" />}
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
